import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import styles from './LawyerProjectThreadPanel.module.css';

const TOGGLE_RIGHT_PX = 0;
const TOGGLE_BOTTOM_PX = 16;
const VIEWPORT_EDGE_PX = 8;
/** Keep in sync with `.threadPanelCollapsed` max-height in module CSS. */
const COLLAPSED_MAX_HEIGHT_PX = 680;
/** Avoid show/hide flicker when height is near the collapse threshold. */
const TOGGLE_HYSTERESIS_PX = 32;
/** Browser smooth scroll is ~500ms; add 300ms per UX request. */
const SCROLL_TO_PAGE_TOP_MS = 800;

/**
 * Natural thread height (unaffected by panel max-height / flex shrink).
 * @param {HTMLElement | null} thread
 */
function getNaturalThreadHeight(thread) {
  if (!thread) {
    return 0;
  }
  let total = 0;
  for (const child of thread.children) {
    const el = child;
    const style = getComputedStyle(el);
    const margin =
      parseFloat(style.marginTop) + parseFloat(style.marginBottom);
    if (el.classList.contains('lawyer-project-thread__conv-body')) {
      total += el.scrollHeight + margin;
    } else {
      total += el.offsetHeight + margin;
    }
  }
  return total;
}

/**
 * @param {HTMLElement | null} el
 * @returns {HTMLElement | null}
 */
function findScrollParent(el) {
  let node = el?.parentElement;
  while (node) {
    const {overflowY} = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * @param {number} t 0..1
 */
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

/**
 * @param {HTMLElement} el
 * @param {number} targetTop
 * @param {number} durationMs
 */
function animateElementScrollTop(el, targetTop, durationMs) {
  const startTop = el.scrollTop;
  const distance = targetTop - startTop;
  if (distance === 0) {
    return;
  }
  const startTime = performance.now();

  const step = (now) => {
    const elapsed = now - startTime;
    const progress = Math.min(1, elapsed / durationMs);
    el.scrollTop = startTop + distance * easeInOutCubic(progress);
    if (progress < 1) {
      requestAnimationFrame(step);
    }
  };
  requestAnimationFrame(step);
}

/**
 * @param {number} targetTop
 * @param {number} durationMs
 */
function animateWindowScrollTop(targetTop, durationMs) {
  const startTop = window.pageYOffset || document.documentElement.scrollTop;
  const distance = targetTop - startTop;
  if (distance === 0) {
    return;
  }
  const startTime = performance.now();

  const step = (now) => {
    const elapsed = now - startTime;
    const progress = Math.min(1, elapsed / durationMs);
    const nextTop = startTop + distance * easeInOutCubic(progress);
    window.scrollTo(0, nextTop);
    if (progress < 1) {
      requestAnimationFrame(step);
    }
  };
  requestAnimationFrame(step);
}

/**
 * @param {HTMLElement | null} descriptionEl
 */
function scrollToPageTop(descriptionEl) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollParent = descriptionEl ? findScrollParent(descriptionEl) : null;

  if (reducedMotion) {
    if (scrollParent) {
      scrollParent.scrollTop = 0;
    }
    window.scrollTo(0, 0);
    return;
  }

  if (scrollParent) {
    animateElementScrollTop(scrollParent, 0, SCROLL_TO_PAGE_TOP_MS);
  } else {
    animateWindowScrollTop(0, SCROLL_TO_PAGE_TOP_MS);
  }
}

/**
 * Expand/collapse wrapper for the CRM project message thread inside `.description`.
 * Toggle stays absolute on the panel when visible; pins fixed to viewport when scrolled off-screen.
 * @param {{ children: React.ReactNode; defaultExpanded?: boolean }} props
 */
export default function LawyerProjectThreadPanel({children, defaultExpanded = false}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showToggle, setShowToggle] = useState(true);
  const [pinned, setPinned] = useState(false);
  const [fixedOffsets, setFixedOffsets] = useState({bottom: TOGGLE_BOTTOM_PX, right: TOGGLE_RIGHT_PX});
  const panelRef = useRef(null);
  const anchorRef = useRef(null);
  const measureRafRef = useRef(null);

  const measureNeedsToggle = useCallback(() => {
    const panel = panelRef.current;
    const thread = panel?.querySelector('.lawyerProjectThread');
    if (!thread) {
      setShowToggle((prev) => (prev === false ? prev : false));
      return;
    }

    const naturalHeight = getNaturalThreadHeight(thread);
    const showAt = COLLAPSED_MAX_HEIGHT_PX + TOGGLE_HYSTERESIS_PX;
    const hideAt = COLLAPSED_MAX_HEIGHT_PX - TOGGLE_HYSTERESIS_PX;

    setShowToggle((prev) => {
      if (naturalHeight > showAt) {
        return prev === true ? prev : true;
      }
      if (naturalHeight < hideAt) {
        return prev === false ? prev : false;
      }
      return prev;
    });
  }, []);

  const scheduleMeasureNeedsToggle = useCallback(() => {
    if (measureRafRef.current != null) {
      cancelAnimationFrame(measureRafRef.current);
    }
    measureRafRef.current = requestAnimationFrame(() => {
      measureRafRef.current = null;
      measureNeedsToggle();
    });
  }, [measureNeedsToggle]);

  const updateTogglePosition = useCallback(() => {
    if (!showToggle) {
      setPinned(false);
      return;
    }
    const panelEl = panelRef.current;
    const anchorEl = anchorRef.current;
    if (!panelEl || !anchorEl) {
      return;
    }

    const descriptionEl = panelEl.closest('.description');
    if (!descriptionEl) {
      setPinned(false);
      return;
    }

    const anchorRect = anchorEl.getBoundingClientRect();
    const shouldPin = anchorRect.bottom > window.innerHeight - VIEWPORT_EDGE_PX;

    if (!shouldPin) {
      setPinned(false);
      return;
    }

    // Match absolute anchor position (inside description padding), not the outer desc box edge.
    setPinned(true);
    setFixedOffsets({
      bottom: Math.max(TOGGLE_BOTTOM_PX, window.innerHeight - anchorRect.bottom),
      right: Math.max(TOGGLE_RIGHT_PX, window.innerWidth - anchorRect.right),
    });
  }, [showToggle]);

  useLayoutEffect(() => {
    scheduleMeasureNeedsToggle();
  }, [scheduleMeasureNeedsToggle, children]);

  useEffect(() => {
    const panel = panelRef.current;
    const thread = panel?.querySelector('.lawyerProjectThread');
    const convBody = thread.querySelector('.lawyer-project-thread__conv-body');
    const composer = thread.querySelector('.lawyer-project-composer__root');
    const header = thread.querySelector('header');
    if (!thread) {
      return undefined;
    }

    const ro = new ResizeObserver(() => {
      scheduleMeasureNeedsToggle();
    });
    ro.observe(thread);
    if (convBody) {
      ro.observe(convBody);
    }
    if (composer) {
      ro.observe(composer);
    }
    if (header) {
      ro.observe(header);
    }
    const mo =
      convBody &&
      new MutationObserver(() => {
        scheduleMeasureNeedsToggle();
      });
    if (mo && convBody) {
      mo.observe(convBody, {childList: true, subtree: true, characterData: true});
    }

    window.addEventListener('resize', scheduleMeasureNeedsToggle);
    return () => {
      ro.disconnect();
      mo?.disconnect();
      window.removeEventListener('resize', scheduleMeasureNeedsToggle);
      if (measureRafRef.current != null) {
        cancelAnimationFrame(measureRafRef.current);
        measureRafRef.current = null;
      }
    };
  }, [scheduleMeasureNeedsToggle, children]);

  useEffect(() => {
    if (!showToggle) {
      setPinned(false);
      return undefined;
    }
    updateTogglePosition();
    window.addEventListener('scroll', updateTogglePosition, true);
    window.addEventListener('resize', updateTogglePosition);
    return () => {
      window.removeEventListener('scroll', updateTogglePosition, true);
      window.removeEventListener('resize', updateTogglePosition);
    };
  }, [updateTogglePosition, expanded, showToggle]);

  const toggleStyle = pinned
    ? {
        bottom: `${fixedOffsets.bottom}px`,
        right: `${fixedOffsets.right}px`,
      }
    : undefined;

  const handleToggleClick = () => {
    setExpanded((wasExpanded) => {
      if (wasExpanded) {
        requestAnimationFrame(() => {
          const descriptionEl = panelRef.current?.closest('.description');
          scrollToPageTop(descriptionEl);
        });
      }
      return !wasExpanded;
    });
  };

  const isCollapsed = showToggle && !expanded;

  return (
    <div
      ref={panelRef}
      className={`${styles.threadPanel} lawyer-project-thread-panel ${
        isCollapsed ? styles.threadPanelCollapsed : styles.threadPanelExpanded
      }`}
    >
      <div className={styles.threadPanelInner}>{children}</div>
      {isCollapsed ? <div className={styles.threadPanelFade} aria-hidden="true" /> : null}
      {showToggle ? (
        <>
          <div ref={anchorRef} className={styles.threadPanelToggleAnchor} aria-hidden="true" />
          <button
            type="button"
            className={`${styles.threadPanelToggle} lawyer-project-thread-panel__toggle ${
              pinned ? styles.threadPanelTogglePinned : ''
            }`}
            style={toggleStyle}
            onClick={handleToggleClick}
            aria-expanded={expanded}
            aria-label={expanded ? 'Minimize conversation' : 'Expand conversation'}
            title={expanded ? 'Minimize conversation' : 'Expand conversation'}
          >
            <i className={`fa ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
          </button>
        </>
      ) : null}
    </div>
  );
}
